import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field, SmartField, Segmented } from '../components/Inputs'
import MulchWizard from '../components/MulchWizard'
import type { WizardGoal } from '../components/MulchWizard'
import { computeMulch, computeMulchCoverage, DEFAULT_MULCH } from '../calculators/mulch'
import type { MulchInputs, MulchArea } from '../calculators/mulch'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/mulch/'
const CATEGORY = 'outdoor-and-landscaping'

interface CalcContent {
  title: string
  desc: string
  h1: string
  intro: string
  savePrefix: string
  bed: string
  addBed: string
  depth: string
  depthTip: string
  bagSize: string
  bagSizeTip: string
  addFabric: string
  extraAllowance: string
  extraTip: string
  extraNote: string
  primaryName: string
  primaryLabel: string
  aboutH2: string
  aboutP1: string
  howH2: string
  howP1: string
  formula: string
  howP2: string
  mistakesH3: string
  relatedH2: string
  reviewer: string
  faqs: QA[]
}

const EN: CalcContent = {
  title: 'Mulch Calculator — How Much Mulch Do I Need? | RenoSheets',
  desc: 'Free mulch calculator. Enter bed size and depth for cubic yards (bulk) or the number of bags, plus landscape fabric and edging. No signup.',
  h1: 'Mulch Calculator',
  intro: 'Add your beds for the cubic yards of bulk mulch and the bag equivalent — plus landscape fabric, staples and edging.',
  savePrefix: 'Mulch',
  bed: 'Bed',
  addBed: '+ Add another bed',
  depth: 'Depth',
  depthTip: 'How deep to spread the mulch. 2–3 inches suits most beds. Keep depth to 4 in max to avoid smothering roots.',
  bagSize: 'Bag size',
  bagSizeTip: 'Most bagged mulch is sold in 2 cu ft (≈ 57 L) bags. Some premium brands use 3 cu ft (≈ 85 L). Toggle the unit to match what\'s on your bag.',
  addFabric: 'Add landscape fabric underneath',
  extraAllowance: 'Extra allowance',
  extraTip: 'Optional buffer for settling and uneven beds. 10% is plenty for mulch since it compresses slightly over time.',
  extraNote: 'Adjust to your beds — we never pad quantities.',
  primaryName: 'Bulk mulch',
  primaryLabel: 'of mulch (bulk order)',
  aboutH2: 'What actually affects how much mulch you need',
  aboutP1: 'Depth needs are different for a fresh bed versus a yearly refresh, and using the same number for both leads to over-ordering. A brand-new bed typically needs 3–4 inches to properly suppress weeds and retain moisture. An established bed that already has mulch from previous years usually only needs a 1–2 inch top-up, since the existing layer hasn\'t fully broken down — going deeper than 4 inches total risks smothering roots and trapping excess moisture against the trunk or stems. Mulch type affects how often you\'ll be back: shredded hardwood and pine bark break down over a season or two and need regular refreshing, while inorganic options like rubber or stone barely decompose but don\'t feed the soil the way organic mulch does. Bulk delivery almost always beats bagged mulch in cost once you\'re past 2–3 cubic yards, but bagged is easier to work with for small or oddly shaped beds where a wheelbarrow run isn\'t practical.',
  howH2: 'How to calculate how much mulch you need',
  howP1: 'Mulch is a simple volume: bed area × depth, then convert to cubic yards or count bags.',
  formula: `cubic feet = total area (sq ft) × (depth in ÷ 12)
cubic yards = cubic feet ÷ 27
bags (2 cu ft) = cubic feet ÷ 2`,
  howP2: 'Add multiple beds and they are summed. Bulk delivery wins past roughly 2–3 cubic yards (10–12 bags), though prices vary. Always check whether your supplier\'s bulk price beats the bagged equivalent at the big-box store.',
  mistakesH3: 'Common mistakes',
  relatedH2: 'Related calculators',
  reviewer: 'Reviewed by the RenoSheets team for calculation accuracy. Method: area × depth ÷ 27 for cubic yards; bags from bag volume with user-adjustable extra allowance. Last updated 2026-05-23. Estimates only — verify before purchasing.',
  faqs: [
    {
      q: 'How much mulch do I need for a 10×10 garden bed?',
      a: 'A 10 × 10 ft bed at 3 inches deep needs 25 cubic feet of mulch — about 0.93 cubic yards or 13 bags of 2 cubic feet each (with 10% extra). For 2 inch depth the same bed needs roughly 8–9 bags.',
    },
    {
      q: 'How deep should mulch be?',
      a: 'Two to three inches is ideal for most beds. That is enough to suppress weeds and hold moisture without smothering plant roots. Never pile mulch against trunks or stems ("mulch volcano") — keep 2–3 in of clearance. Top up to 3 in every spring as the existing layer breaks down.',
    },
    {
      q: 'How many bags of mulch are in a cubic yard?',
      a: 'A cubic yard is 27 cubic feet. The standard 2 cu ft bag takes 13.5 bags to equal 1 cubic yard. A 3 cu ft bag takes 9 bags. Bulk mulch (ordered by the cubic yard) is almost always cheaper once you need more than about 10–12 bags.',
    },
    {
      q: 'Should I use bags or bulk mulch?',
      a: 'Use bags for small beds (under 2–3 cubic yards). For larger jobs, bulk mulch delivered by the yard is significantly cheaper and saves multiple trips to the store. The break-even is typically around 10–12 bags, though it varies by supplier.',
    },
    {
      q: 'Do I need landscape fabric under mulch?',
      a: 'In planting beds, fabric often does more harm than good long-term — it prevents natural soil improvement and can become a weed trap as it breaks down. A thick layer of mulch (3 in) suppresses weeds effectively on its own. Fabric works better under gravel or rock paths where you do not want the soil to mix.',
    },
    {
      q: 'What is the best mulch depth for weed control?',
      a: 'Three inches of wood chip or shredded bark mulch blocks most weed germination. Research shows that at 3 in, weed emergence drops by over 90%. Four inches is even better, but beyond 4 in mulch can compact, reduce aeration, and hold too much moisture near plant stems.',
    },
    {
      q: 'How do I calculate cubic yards of mulch?',
      a: 'Multiply the area in square feet by the depth in feet, then divide by 27. For example: 200 sq ft × (3 in ÷ 12 in/ft) ÷ 27 = 200 × 0.25 ÷ 27 = 1.85 cu yd. Or just enter your beds and depth above — the calculator does this automatically.',
    },
  ],
}

const FR: CalcContent = {
  title: 'Calculateur de paillis — Quelle quantité me faut-il? | RenoSheets',
  desc: 'Calculateur de paillis gratuit. Entrez la taille des plates-bandes et la profondeur pour les verges cubes (en vrac) ou le nombre de sacs, plus la membrane géotextile et la bordure. Sans inscription.',
  h1: 'Calculateur de paillis',
  intro: 'Ajoutez vos plates-bandes pour les verges cubes de paillis en vrac et l’équivalent en sacs — plus la membrane géotextile, les agrafes et la bordure.',
  savePrefix: 'Paillis',
  bed: 'Plate-bande',
  addBed: '+ Ajouter une plate-bande',
  depth: 'Profondeur',
  depthTip: 'À quelle profondeur étaler le paillis. 2–3 pouces conviennent à la plupart des plates-bandes. Limitez la profondeur à 4 po au maximum pour ne pas étouffer les racines.',
  bagSize: 'Taille du sac',
  bagSizeTip: 'La plupart du paillis en sac se vend en sacs de 2 pi³ (≈ 57 L). Certaines marques haut de gamme utilisent des sacs de 3 pi³ (≈ 85 L). Basculez l’unité pour correspondre à votre sac.',
  addFabric: 'Ajouter la membrane géotextile en dessous',
  extraAllowance: 'Marge supplémentaire',
  extraTip: 'Tampon facultatif pour le tassement et les plates-bandes inégales. 10 % suffit amplement pour le paillis, car il se compresse légèrement avec le temps.',
  extraNote: 'Ajustez selon vos plates-bandes — nous ne gonflons jamais les quantités.',
  primaryName: 'Paillis en vrac',
  primaryLabel: 'de paillis (commande en vrac)',
  aboutH2: 'Ce qui influence vraiment la quantité de paillis nécessaire',
  aboutP1: 'Les besoins en profondeur diffèrent entre un nouveau lit et un rafraîchissement annuel, et utiliser le même chiffre pour les deux mène à une surcommande. Un lit tout neuf a habituellement besoin de 3 à 4 pouces pour bien supprimer les mauvaises herbes et retenir l’humidité. Un lit établi qui a déjà du paillis des années précédentes n’a habituellement besoin que d’un pouce ou deux de plus, puisque la couche existante ne s’est pas complètement décomposée — dépasser 4 pouces au total risque d’étouffer les racines et de retenir trop d’humidité contre le tronc ou les tiges. Le type de paillis détermine la fréquence des rafraîchissements : le bois dur déchiqueté et l’écorce de pin se décomposent en une saison ou deux et demandent un renouvellement régulier, tandis que les options inorganiques comme le caoutchouc ou la pierre se décomposent à peine mais ne nourrissent pas le sol comme le paillis organique. La livraison en vrac coûte presque toujours moins cher que le paillis en sac au-delà de 2 à 3 verges cubes, mais le sac est plus facile à manier pour de petits lits ou des formes irrégulières où une brouette n’est pas pratique.',
  howH2: 'Comment calculer la quantité de paillis nécessaire',
  howP1: 'Le paillis est un volume simple : surface de la plate-bande × profondeur, puis conversion en verges cubes ou en nombre de sacs.',
  formula: `pieds cubes = surface totale (pi²) × (profondeur po ÷ 12)
verges cubes = pieds cubes ÷ 27
sacs (2 pi³) = pieds cubes ÷ 2`,
  howP2: 'Ajoutez plusieurs plates-bandes et elles s’additionnent. La livraison en vrac l’emporte au-delà d’environ 2–3 verges cubes (10–12 sacs), même si les prix varient. Vérifiez toujours si le prix en vrac de votre fournisseur bat l’équivalent en sacs du grand détaillant.',
  mistakesH3: 'Erreurs fréquentes',
  relatedH2: 'Calculateurs connexes',
  reviewer: 'Révisé par l’équipe RenoSheets pour l’exactitude des calculs. Méthode : surface × profondeur ÷ 27 pour les verges cubes; sacs selon le volume du sac avec une marge supplémentaire ajustable. Dernière mise à jour 2026-05-23. Estimations seulement — vérifiez avant d’acheter.',
  faqs: [
    {
      q: 'Combien de paillis me faut-il pour une plate-bande de 10 × 10?',
      a: 'Une plate-bande de 10 × 10 pi à 3 pouces de profondeur demande 25 pieds cubes de paillis — environ 0,93 verge cube ou 13 sacs de 2 pieds cubes chacun (avec 10 % en plus). À 2 pouces de profondeur, la même plate-bande demande à peu près 8–9 sacs.',
    },
    {
      q: 'Quelle profondeur doit avoir le paillis?',
      a: 'De deux à trois pouces est idéal pour la plupart des plates-bandes. C’est suffisant pour supprimer les mauvaises herbes et retenir l’humidité sans étouffer les racines des plantes. N’empilez jamais le paillis contre les troncs ou les tiges (« volcan de paillis ») — gardez 2–3 po de dégagement. Rechargez à 3 po chaque printemps à mesure que la couche existante se décompose.',
    },
    {
      q: 'Combien de sacs de paillis y a-t-il dans une verge cube?',
      a: 'Une verge cube fait 27 pieds cubes. Le sac standard de 2 pi³ demande 13,5 sacs pour égaler 1 verge cube. Un sac de 3 pi³ en demande 9. Le paillis en vrac (commandé à la verge cube) est presque toujours moins cher dès qu’il vous faut plus d’environ 10–12 sacs.',
    },
    {
      q: 'Devrais-je utiliser des sacs ou du paillis en vrac?',
      a: 'Utilisez des sacs pour les petites plates-bandes (moins de 2–3 verges cubes). Pour les plus gros travaux, le paillis en vrac livré à la verge est nettement moins cher et évite plusieurs allers-retours au magasin. Le point d’équilibre se situe généralement autour de 10–12 sacs, mais cela varie selon le fournisseur.',
    },
    {
      q: 'Ai-je besoin de membrane géotextile sous le paillis?',
      a: 'Dans les plates-bandes de plantation, la membrane fait souvent plus de tort que de bien à long terme — elle empêche l’amélioration naturelle du sol et peut devenir un piège à mauvaises herbes en se décomposant. Une épaisse couche de paillis (3 po) supprime les mauvaises herbes efficacement à elle seule. La membrane convient mieux sous les sentiers de gravier ou de pierre, où l’on ne veut pas que le sol se mélange.',
    },
    {
      q: 'Quelle est la meilleure profondeur de paillis pour contrôler les mauvaises herbes?',
      a: 'Trois pouces de paillis de copeaux de bois ou d’écorce déchiquetée bloquent la majorité de la germination des mauvaises herbes. Les recherches montrent qu’à 3 po, l’émergence des mauvaises herbes chute de plus de 90 %. Quatre pouces, c’est encore mieux, mais au-delà de 4 po le paillis peut se compacter, réduire l’aération et retenir trop d’humidité près des tiges.',
    },
    {
      q: 'Comment calculer les verges cubes de paillis?',
      a: 'Multipliez la surface en pieds carrés par la profondeur en pieds, puis divisez par 27. Par exemple : 200 pi² × (3 po ÷ 12 po/pi) ÷ 27 = 200 × 0,25 ÷ 27 = 1,85 verge cube. Ou entrez simplement vos plates-bandes et la profondeur ci-dessus — le calculateur le fait automatiquement.',
    },
  ],
}

export default function MulchCalculator() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<MulchInputs>(DEFAULT_MULCH)
  const [saved, setSaved] = useState(false)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [wizardGoal, setWizardGoal] = useState<WizardGoal>('forward')
  const r = useMemo(() => computeMulch(i, locale), [i, locale])
  // Reverse path: how much area do `bagsHave` bags cover at `depthIn`?
  // Cheap inline math — only re-derived when the relevant inputs change.
  const rev = useMemo(
    () => computeMulchCoverage(i.bagsHave, i.bagCuFt, i.depthIn),
    [i.bagsHave, i.bagCuFt, i.depthIn],
  )
  const set = (patch: Partial<MulchInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }
  const setArea = (idx: number, patch: Partial<MulchArea>) =>
    set({ areas: i.areas.map((a, k) => (k === idx ? { ...a, ...patch } : a)) })
  const addArea = () => set({ areas: [...i.areas, { length: 8, width: 8 }] })
  const removeArea = (idx: number) => set({ areas: i.areas.filter((_, k) => k !== idx) })
  const metric = i.unit === 'metric'
  const lin = metric ? 'm' : (locale === 'fr' ? 'pi' : 'ft')
  const inUnit = locale === 'fr' ? 'po' : 'in'
  const cuFtUnit = locale === 'fr' ? 'pi³' : 'cu ft'

  // Volume display tracks the unit system: cu ft when imperial (matches the
  // "Feet" toggle), m³ when metric. Note: bulk mulch is often *ordered* by
  // the cubic yard — that conversion is mentioned in the assumptions text.
  const volumeQty = metric ? r.cubicMeters : r.cubicFeetTotal
  const volumeUnit = metric ? 'm³' : cuFtUnit

  // Bag size: stored as cu ft internally, displayed in user's chosen unit (cu ft or L).
  // 1 cu ft = 28.3168466 L. Rounded display so 2 cu ft = 56.6 L (not 56.633693…).
  const CUFT_TO_L = 28.3168466
  const bagDisplayVal = i.bagUnit === 'L'
    ? Math.round(i.bagCuFt * CUFT_TO_L * 10) / 10
    : i.bagCuFt
  const bagDisplayUnit = i.bagUnit === 'L' ? 'L' : cuFtUnit
  const setBagFromInput = (n: number) => {
    if (i.bagUnit === 'L') {
      // User typed Litres → convert to cu ft for internal math.
      set({ bagCuFt: n / CUFT_TO_L })
    } else {
      set({ bagCuFt: n })
    }
  }

  // Secondary (bags) tracks the user-selected bag size and shows it in the user's chosen unit.
  const bagsName = locale === 'fr' ? `Sacs de ${bagDisplayVal} ${bagDisplayUnit}` : `${bagDisplayVal} ${bagDisplayUnit} bags`
  const bagsLabel = `${bagDisplayVal} ${bagDisplayUnit}`

  const onSave = async () => {
    const areaUnit = metric ? 'm²' : (locale === 'fr' ? 'pi²' : 'sq ft')
    const areaVal = metric ? r.areaM2 : r.areaSqft
    const volWord = metric ? 'm³' : cuFtUnit
    const bagWord = locale === 'fr' ? `sacs de ${bagDisplayVal} ${bagDisplayUnit}` : `bags of ${bagDisplayVal} ${bagDisplayUnit}`
    await saveSheet({
      id: genId(),
      type: 'mulch',
      name: `${c.savePrefix} — ${areaVal} ${areaUnit}`,
      createdAt: new Date().toISOString(),
      summary: locale === 'fr'
        ? `${volumeQty} ${volWord} en vrac OU ${r.bags} ${bagWord}`
        : `${volumeQty} ${volWord} bulk OR ${r.bags} ${bagWord}`,
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'mulch')

  const bedsWord = (n: number) =>
    locale === 'fr' ? `${n} plate-bande${n === 1 ? '' : 's'}` : `${n} bed${n === 1 ? '' : 's'}`
  // In 'area' mode the user didn't enter bed cards, so the "across N beds" clause
  // doesn't apply — collapse it to a blank string.
  const bedsClause = i.inputMode === 'dimensions'
    ? (locale === 'fr' ? ` sur ${bedsWord(i.areas.length)}` : ` across ${bedsWord(i.areas.length)}`)
    : ''
  const assumptions = (() => {
    if (metric) {
      // Depth input stays in inches; convert for the metric assumptions sentence only.
      const depthCm = (i.depthIn * 2.54).toFixed(1).replace(/\.0$/, '')
      const rawM3 = (r.cubicFeet * 0.028316846592).toFixed(2)
      return locale === 'fr'
        ? `${r.areaM2} m²${bedsClause} à ${depthCm} cm de profondeur = ${rawM3} m³, ${i.wastePct} % en plus.`
        : `${r.areaM2} m²${bedsClause} at ${depthCm} cm deep = ${rawM3} m³, ${i.wastePct}% extra.`
    }
    return locale === 'fr'
      ? `${r.areaSqft} pi²${bedsClause} à ${i.depthIn} po de profondeur = ${r.cubicFeet} pi³, ${i.wastePct} % en plus.`
      : `${r.areaSqft} sq ft${bedsClause} at ${i.depthIn} in deep = ${r.cubicFeet} cu ft, ${i.wastePct}% extra.`
  })()

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Mirrors computeMulch: sum beds, × depth (in/12), × (1 + waste%), ÷ 27 for
  // cu yd, ÷ bag size for the bag count.
  const sqftWord = locale === 'fr' ? 'pi²' : 'sq ft'
  const cuYdWord = locale === 'fr' ? 'verges cubes' : 'cu yd'
  const buildExplain = (): React.ReactNode => (
    <ol className="list-decimal space-y-1 pl-5">
      <li>
        {i.inputMode === 'area'
          ? (locale === 'fr' ? 'Surface saisie' : 'Area you entered')
          : (locale === 'fr'
              ? `Surface totale (${bedsWord(i.areas.length)})`
              : `Total area (${bedsWord(i.areas.length)})`)}:
        {' '}<strong>{r.areaSqft} {sqftWord}</strong>
      </li>
      <li>
        {locale === 'fr' ? `× profondeur ${i.depthIn}/12 pi` : `× depth ${i.depthIn}/12 ft`}:
        {' '}<strong>{r.cubicFeet} {cuFtUnit}</strong>
      </li>
      <li>{locale === 'fr' ? `+ ${i.wastePct} % en plus` : `+ ${i.wastePct}% extra`}: <strong>{r.cubicFeetTotal} {cuFtUnit}</strong></li>
      <li>{locale === 'fr' ? '÷ 27 = livraison en vrac' : '÷ 27 = bulk order'}: <strong>{r.cubicYards} {cuYdWord}</strong></li>
      <li>
        {locale === 'fr' ? `÷ ${i.bagCuFt} pi³ par sac` : `÷ ${i.bagCuFt} cu ft per bag`}
        {' '}= <strong>{r.bags} {locale === 'fr' ? (r.bags === 1 ? 'sac' : 'sacs') : (r.bags === 1 ? 'bag' : 'bags')}</strong>
      </li>
    </ol>
  )

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={c.title} description={c.desc} path={PATH} schema={softwareAppSchema(calcName('mulch', 'Mulch Calculator', locale), c.desc, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Outdoor & Landscaping', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('mulch', 'Mulch Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-1 max-w-content text-text-muted">{c.intro}</p>

      {/* Entry banner — only shown when wizard is closed, invites first-timers
          to take the quiz instead of staring at the full form.
          Dark-green CTA with hover lift, arrow slide, and shadow boost. */}
      {!wizardOpen && (
        <button
          type="button"
          onClick={() => setWizardOpen(true)}
          className="group mt-4 flex w-full items-center justify-between rounded-lg bg-green-800 px-5 py-4 text-left text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-green-700 hover:shadow-xl sm:w-auto"
        >
          <span className="flex items-center gap-3">
            <span className="text-xl transition-transform duration-200 group-hover:rotate-12" aria-hidden>✨</span>
            <span>
              <span className="block text-sm font-semibold">{locale === 'fr' ? 'Première fois? Essayez le quiz guidé' : 'First time? Try the guided quiz'}</span>
              <span className="block text-xs text-white/80">{locale === 'fr' ? '~30 secondes — répond à votre place' : '~30 seconds — fills the calculator for you'}</span>
            </span>
          </span>
          <span className="ml-3 text-lg transition-transform duration-200 group-hover:translate-x-1" aria-hidden>→</span>
        </button>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        {wizardOpen ? (
          <MulchWizard
            locale={locale}
            copy={{
              bagSize: c.bagSize,
              bagSizeTip: c.bagSizeTip,
              depth: c.depth,
              depthTip: c.depthTip,
              bed: c.bed,
              addBed: c.addBed,
              remove: tr('calc.remove'),
            }}
            i={i}
            set={set}
            setArea={setArea}
            addArea={addArea}
            removeArea={removeArea}
            goal={wizardGoal}
            setGoal={setWizardGoal}
            onClose={() => setWizardOpen(false)}
          />
        ) : wizardGoal === 'reverse' ? (
          /* Reverse mode after the quiz closes: a compact "what you have" card so
             the coverage answer on the right stays adjustable — and a way back to
             the forward "how many bags do I need?" calculator. */
          <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-text">{locale === 'fr' ? 'Ce que vous avez' : 'What you have'}</h2>
              <button type="button" onClick={() => setWizardGoal('forward')} className="text-xs font-medium text-brand-text hover:underline">
                {locale === 'fr' ? '↔ Calculer les sacs requis' : '↔ Switch to: bags needed'}
              </button>
            </div>
            <div className="space-y-4">
              <div className="rounded-md border border-border p-3">
                <Field
                  label={locale === 'fr' ? 'Nombre de sacs que vous avez' : 'Bags you have'}
                  value={i.bagsHave}
                  onChange={(n) => set({ bagsHave: Math.max(1, Math.round(n)) })}
                  min={1}
                  step={1}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="mb-1 flex items-center justify-between text-sm font-medium text-text-body">
                    <span className="flex items-center">{c.bagSize}<InfoTip text={c.bagSizeTip} /></span>
                    <span className="inline-flex overflow-hidden rounded-md border border-border text-[11px]">
                      <button type="button" onClick={() => set({ bagUnit: 'cuft' })} className={`px-1.5 py-0.5 ${i.bagUnit === 'cuft' ? 'bg-primary text-on-primary' : 'text-text-muted hover:bg-surface-2'}`}>{cuFtUnit}</button>
                      <button type="button" onClick={() => set({ bagUnit: 'L' })} className={`px-1.5 py-0.5 ${i.bagUnit === 'L' ? 'bg-primary text-on-primary' : 'text-text-muted hover:bg-surface-2'}`}>L</button>
                    </span>
                  </span>
                  <Field label="" value={bagDisplayVal} onChange={setBagFromInput} suffix={bagDisplayUnit} />
                </div>
                <div>
                  <span className="mb-1 flex items-center text-sm font-medium text-text-body">{c.depth}<InfoTip text={c.depthTip} /></span>
                  <SmartField label="" imperialValue={i.depthIn} onChangeImperial={(n) => set({ depthIn: n })} imperialUnit="in" />
                </div>
              </div>
            </div>
          </form>
        ) : (
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
          <div className="mb-5 flex flex-wrap gap-6">
            <Segmented
              label={tr('calc.input')}
              value={i.inputMode}
              onChange={(v) => {
                // When toggling Dimensions → Total area, pre-fill the area field
                // with the sum of bed L × W (in the user's current unit) so they
                // see their running total. Easy to override afterward.
                if (v === 'area' && i.inputMode === 'dimensions') {
                  const sum = i.areas.reduce(
                    (s, a) => s + Math.max(0, a.length) * Math.max(0, a.width),
                    0,
                  )
                  set({ inputMode: v, totalArea: Math.round(sum * 10) / 10 })
                } else {
                  set({ inputMode: v })
                }
              }}
              options={[{ v: 'dimensions', l: tr('calc.dimensions') }, { v: 'area', l: tr('calc.totalArea') }]}
            />
          </div>

          {i.inputMode === 'dimensions' ? (
            <div className="space-y-3">
              {i.areas.map((a, idx) => (
                <div key={idx} className="rounded-md border border-border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-text-body">{c.bed} {idx + 1}</span>
                    {i.areas.length > 1 && (
                      <button type="button" onClick={() => removeArea(idx)} className="text-xs text-danger hover:underline">{tr('calc.remove')}</button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <SmartField label={tr('calc.length')} imperialValue={a.length} onChangeImperial={(n) => setArea(idx, { length: n })} imperialUnit="ft" />
                    <SmartField label={tr('calc.width')} imperialValue={a.width} onChangeImperial={(n) => setArea(idx, { width: n })} imperialUnit="ft" />
                  </div>
                </div>
              ))}
              <button type="button" onClick={addArea} className="w-full rounded-md border border-dashed border-border py-2 text-sm font-medium text-brand-text hover:bg-surface-2">
                {c.addBed}
              </button>
            </div>
          ) : (
            <div className="rounded-md border border-border p-3">
              <SmartField
                label={tr('calc.totalArea')}
                imperialValue={i.totalArea}
                onChangeImperial={(n) => set({ totalArea: n })}
                imperialUnit="sq ft"
              />
              <p className="mt-2 text-xs text-text-subtle">{tr('calc.areaHint')}</p>
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <span className="mb-1 flex items-center text-sm font-medium text-text-body">
                {c.depth}
                <InfoTip text={c.depthTip} />
              </span>
              <SmartField label="" imperialValue={i.depthIn} onChangeImperial={(n) => set({ depthIn: n })} imperialUnit="in" />
            </div>
            <div>
              <span className="mb-1 flex items-center justify-between text-sm font-medium text-text-body">
                <span className="flex items-center">
                  {c.bagSize}
                  <InfoTip text={c.bagSizeTip} />
                </span>
                {/* Tiny unit toggle — Canadian bags label both cu ft and L. */}
                <span className="inline-flex overflow-hidden rounded-md border border-border text-[11px]">
                  <button
                    type="button"
                    onClick={() => set({ bagUnit: 'cuft' })}
                    className={`px-1.5 py-0.5 ${i.bagUnit === 'cuft' ? 'bg-primary text-on-primary' : 'text-text-muted hover:bg-surface-2'}`}
                  >
                    {cuFtUnit}
                  </button>
                  <button
                    type="button"
                    onClick={() => set({ bagUnit: 'L' })}
                    className={`px-1.5 py-0.5 ${i.bagUnit === 'L' ? 'bg-primary text-on-primary' : 'text-text-muted hover:bg-surface-2'}`}
                  >
                    L
                  </button>
                </span>
              </span>
              <Field label="" value={bagDisplayVal} onChange={setBagFromInput} suffix={bagDisplayUnit} />
            </div>
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm text-text-body">
            <input type="checkbox" checked={i.needFabric} onChange={(e) => set({ needFabric: e.target.checked })} />
            {c.addFabric}
          </label>

          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center font-medium text-text-body">
                {c.extraAllowance}
                <InfoTip text={c.extraTip} />
              </span>
              <span className="tnum text-brand-text">{i.wastePct}%</span>
            </div>
            <input
              type="range" min={0} max={20} step={1} value={i.wastePct}
              onChange={(e) => set({ wastePct: parseInt(e.target.value) })}
              className="mt-1 w-full accent-brand"
            />
            <p className="mt-2 text-xs text-text-subtle">{c.extraNote}</p>
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>
        )}

        <div className="lg:col-span-2">
          {wizardGoal === 'reverse' ? (
            /* Reverse-mode coverage panel — shown whenever the user is in
               'reverse' mode (during the quiz AND after it closes), paired with
               the "what you have" card on the left. */
            <section className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm" aria-live="polite">
              <div className="border-b border-border bg-brand-tint/60 p-5">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  {locale === 'fr' ? 'Votre couverture' : 'Your coverage'}
                </div>
                <div className="tnum mt-1 text-result font-bold text-text">
                  {metric ? rev.coverageM2 : rev.coverageSqft}{' '}
                  <span className="text-2xl font-semibold">{metric ? 'm²' : (locale === 'fr' ? 'pi²' : 'sq ft')}</span>
                </div>
                {/* Even base-size, dark text — matches the forward result card's
                    lines under the headline number (no size/colour gradient). */}
                <div className="mt-2 space-y-1 text-base text-text">
                  <p>
                    {locale === 'fr'
                      ? `de paillis à ${i.depthIn} po de profondeur`
                      : `of mulch at ${i.depthIn} in deep`}
                  </p>
                  <p>
                    {locale === 'fr'
                      ? `Environ une plate-bande carrée de ${metric ? rev.equivSquareM : rev.equivSquareFt} × ${metric ? rev.equivSquareM : rev.equivSquareFt} ${metric ? 'm' : (locale === 'fr' ? 'pi' : 'ft')}.`
                      : `Roughly a square bed of ${rev.equivSquareFt} × ${rev.equivSquareFt} ft (${rev.equivSquareM} × ${rev.equivSquareM} m).`}
                  </p>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-sm font-semibold text-text">
                  {locale === 'fr' ? 'Détail' : 'Breakdown'}
                </h3>
                <ul className="mt-2 space-y-1 text-sm text-text-body">
                  <li>
                    <span className="text-text-muted">{locale === 'fr' ? 'Sacs' : 'Bags'}: </span>
                    <span className="font-medium">{i.bagsHave} × {i.bagCuFt} {locale === 'fr' ? 'pi³' : 'cu ft'} {i.bagUnit === 'L' && `(${Math.round(i.bagCuFt * 28.3168466 * 10) / 10} L ${locale === 'fr' ? 'chacun' : 'each'})`}</span>
                  </li>
                  <li>
                    <span className="text-text-muted">{locale === 'fr' ? 'Volume total' : 'Total volume'}: </span>
                    <span className="font-medium">{rev.totalCubicFeet} {locale === 'fr' ? 'pi³' : 'cu ft'} ({rev.totalLitres} L)</span>
                  </li>
                  <li>
                    <span className="text-text-muted">{locale === 'fr' ? 'Profondeur' : 'Depth'}: </span>
                    <span className="font-medium">{i.depthIn} {locale === 'fr' ? 'po' : 'in'} ({(i.depthIn * 2.54).toFixed(1)} cm)</span>
                  </li>
                </ul>
                <p className="mt-3 text-xs text-text-subtle">
                  {locale === 'fr'
                    ? 'Estimations seulement — la couverture réelle varie selon le tassement et la régularité.'
                    : 'Estimates only — actual coverage varies with settling and surface evenness.'}
                </p>
              </div>
            </section>
          ) : (
            <ResultSheet
              primary={{
                name: c.primaryName,
                label: c.primaryLabel,
                qty: volumeQty,
                unit: volumeUnit,
                query: 'bulk mulch delivery',
              }}
              secondary={
                r.bags > 0
                  ? { name: bagsName, label: bagsLabel, qty: r.bags, unit: 'bag', query: `mulch ${i.bagCuFt} cubic feet bags` }
                  : null
              }
              lines={r.lines}
              assumptions={assumptions}
              explain={buildExplain()}
            />
          )}
        </div>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{c.aboutH2}</h2>
        <p className="mt-3">{c.aboutP1}</p>
        <h2 className="mt-8 text-2xl">{c.howH2}</h2>
        <p className="mt-3">{c.howP1}</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{c.formula}</pre>
        <p className="mt-3">{c.howP2}</p>
        <h3 className="mt-6 text-xl">{c.mistakesH3}</h3>
        {locale === 'fr' ? (
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>Étaler une couche trop <strong>épaisse</strong> — plus de 4 pouces peut se compacter, réduire l’aération et faire pourrir les tiges.</li>
            <li>Acheter du paillis en sac alors que la <strong>livraison en vrac</strong> serait moins chère pour les grandes plates-bandes.</li>
            <li>Empiler le paillis contre les <strong>troncs et les tiges</strong> — cela emprisonne l’humidité et cause la pourriture (« volcan de paillis »).</li>
            <li>Utiliser une membrane dans les <strong>plates-bandes de plantation</strong> — elle finit par piéger les graines de mauvaises herbes en surface et devient le problème.</li>
          </ul>
        ) : (
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>Spreading too <strong>deep</strong> — more than 4 inches can compact, reduce aeration, and rot stems.</li>
            <li>Buying bagged mulch when <strong>bulk delivery</strong> would be cheaper for larger beds.</li>
            <li>Piling mulch against <strong>trunks and stems</strong> — this traps moisture and causes rot ("mulch volcano").</li>
            <li>Using fabric in <strong>planting beds</strong> — it eventually traps weed seeds on top and becomes the problem.</li>
          </ul>
        )}
      </article>

      <Faq items={c.faqs} />

      <section className="mt-10">
        <h2 className="text-2xl">{c.relatedH2}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">{c.reviewer}</div>
    </div>
  )
}
